import { useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";

const steps = [
  {
    num: "01",
    label: "Sign up",
    title: "Sign up & hand over your menu",
    body: "Send us your menu, your logo and your brand colors. One short call, one upload — that is all we need from you. No IT knowledge required, ever.",
    img: "https://images.unsplash.com/photo-1766832255363-c9f060ade8b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHw0fHxyZXN0YXVyYW50JTIwY2hlZiUyMGRhcmslMjBwcmVtaXVtfGVufDB8fHx8MTc4NjgyMTc4NHww&ixlib=rb-4.1.0&q=85",
  },
  {
    num: "02",
    label: "We build",
    title: "We make your shop ready",
    body: "Your homepage, your ordering system and an SEO-friendly website on your own domain — designed, built and live in under 7 days. You review, we polish.",
    img: "https://images.unsplash.com/photo-1621494548002-bfc916172ead?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxyZXN0YXVyYW50JTIwY2hlZiUyMGRhcmslMjBwcmVtaXVtfGVufDB8fHx8MTc4NjgyMTc4NHww&ixlib=rb-4.1.0&q=85",
  },
  {
    num: "03",
    label: "You earn",
    title: "You earn money from day one",
    body: "A modern, custom-made ordering system with 0% commission. Every order, every franc and every customer relationship stays exactly where it belongs — with you.",
    img: "https://images.unsplash.com/photo-1695654390723-479197a8c4a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxmb29kJTIwZGVsaXZlcnklMjBkYXJrJTIwY2luZW1hdGljfGVufDB8fHx8MTc4NjgyMTc4NHww&ixlib=rb-4.1.0&q=85",
  },
  {
    num: "04",
    label: "Apps drive growth",
    title: "Your apps drive more clients",
    body: "Branded iOS & Android apps in the app stores under your name. Push deals, one-tap re-ordering and saved favorites turn occasional guests into weekly regulars.",
    img: "https://images.unsplash.com/photo-1651804805680-3565ef7807f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxzbWFydHBob25lJTIwYXBwJTIwdXNlciUyMGluJTIwZGFya3xlbnwwfHx8fDE3ODY4MjE3ODR8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    num: "05",
    label: "Marketing on autopilot",
    title: "Re-engage clients automatically",
    body: "Built-in auto-marketing works while you cook: newsletters, SMS, automatic cart reminders and loyalty points bring your regulars back before the platforms can steal them.",
    img: "https://images.unsplash.com/photo-1621494548002-bfc916172ead?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxyZXN0YXVyYW50JTIwY2hlZiUyMGRhcmslMjBwcmVtaXVtfGVufDB8fHx8MTc4NjgyMTc4NHww&ixlib=rb-4.1.0&q=85",
  },
];

export default function HowItWorks() {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setActive(Math.min(steps.length - 1, Math.max(0, Math.floor(v * steps.length))));
  });

  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="bg-white border-y border-ink/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-28 lg:pt-40">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="font-mono text-xs tracking-[0.3em] uppercase text-brand mb-6"
        >
          How it works
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-display font-black uppercase tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl max-w-4xl text-ink"
        >
          From signup to first order <span className="text-steel">in 7 days.</span>
        </motion.h2>
      </div>

      <div ref={ref} className="relative max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 pb-24">
        <div className="hidden lg:block">
          <div className="sticky top-[70px] h-[calc(100vh-70px)] flex items-center">
            <div className="relative w-full aspect-[4/5] max-h-[72vh] rounded-2xl overflow-hidden border border-ink/10 card-glow">
              <AnimatePresence mode="wait">
                <motion.img
                  key={active}
                  src={steps[active].img}
                  alt={steps[active].title}
                  initial={{ opacity: 0, scale: 1.06 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="absolute inset-0 w-full h-full object-cover spotlight-img"
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-black/50" />
              <div className="absolute bottom-0 left-0 right-0 p-10">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={active}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.4 }}
                  >
                    <div data-testid="how-active-num" className="font-display font-black text-8xl leading-none text-white/95">{steps[active].num}</div>
                    <div className="mt-3 font-mono text-xs tracking-[0.25em] uppercase text-ice">{steps[active].label}</div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        <div>
          {steps.map((s, i) => (
            <div key={s.num} data-testid={`how-step-${s.num}`} className="min-h-[70vh] flex items-center">
              <div className={`w-full rounded-xl border p-10 transition-[border-color,background-color] duration-500 ${active === i ? "border-brand bg-brand/5 card-glow" : "border-ink/10 bg-[#F5F8FC]"}`}>
                <span className="font-mono text-sm text-brand">{s.num}</span>
                <h3 className="mt-4 font-display font-black uppercase tracking-tight text-2xl lg:text-3xl text-ink">{s.title}</h3>
                <p className="mt-5 text-ink/65 font-light leading-relaxed text-base lg:text-lg">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
